
import { useOptimizedCart } from '@/contexts/OptimizedCartContext';
import { useToast } from '@/hooks/use-toast';
import { useCallback } from 'react';

export const useEnhancedCart = () => {
  const cart = useOptimizedCart();
  const { toast } = useToast();

  const addToCartWithNotification = useCallback(async (product: any, quantity: number = 1) => {
    try {
      // Check stock before adding
      if (product.stock_quantity !== null && product.stock_quantity < quantity) {
        toast({
          title: 'Limited Stock',
          description: `Only ${product.stock_quantity} items available`,
          variant: 'destructive',
        });
        return false;
      }

      // Add to cart
      await cart.addToCart(product, quantity);
      
      // Show success notification
      toast({
        title: 'Added to Cart',
        description: `${product.name} has been added to your cart`,
      });

      return true;
    } catch (error) {
      toast({
        title: 'Failed to Add Item',
        description: 'Please try again',
        variant: 'destructive',
      });
      return false;
    }
  }, [cart, toast]);

  const removeFromCartWithNotification = useCallback(async (productId: string, productName?: string) => {
    try {
      const removedItem = cart.items.find(item => item.product_id === productId);
      if (!removedItem) {
        toast({
          title: 'Item Not Found',
          description: 'Item not found in cart',
          variant: 'destructive',
        });
        return;
      }
      
      await cart.removeFromCart(removedItem.id);
      
      toast({
        title: 'Removed from Cart',
        description: `${productName || removedItem?.name || 'Item'} has been removed from your cart`,
      });
    } catch (error) {
      toast({
        title: 'Failed to Remove Item',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  }, [cart, toast]);

  const updateQuantityWithNotification = useCallback(async (productId: string, newQuantity: number, productName?: string) => {
    try {
      if (newQuantity <= 0) {
        await removeFromCartWithNotification(productId, productName);
        return;
      }

      const item = cart.items.find(item => item.product_id === productId);
      if (!item) {
        toast({
          title: 'Item Not Found',
          description: 'Item not found in cart',
          variant: 'destructive',
        });
        return;
      }
      
      if (item.stock_quantity !== null && item.stock_quantity < newQuantity) {
        toast({
          title: 'Limited Stock',
          description: `Only ${item.stock_quantity} items available`,
          variant: 'destructive',
        });
        return;
      }

      await cart.updateQuantity(item.id, newQuantity);
      
      toast({
        title: 'Cart Updated',
        description: `Updated quantity for ${productName || item?.name || 'item'}`,
      });
    } catch (error) {
      toast({
        title: 'Failed to Update Quantity',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  }, [cart, toast, removeFromCartWithNotification]);

  return {
    ...cart,
    addToCartWithNotification,
    removeFromCartWithNotification,
    updateQuantityWithNotification,
  };
};
